import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Badge, Text, Tooltip, useDateTimeFormat, useLocalizeMessage } from 'libs.nucleus';
const STATUS_VARIANT = {
    scheduled: 'new',
    completed: 'success',
    missed: 'incomplete',
};
const NOTIF_VARIANT = {
    queued: 'new',
    sent: 'success',
    failed: 'incomplete',
    canceled: 'common',
};
const EM_DASH = '—';
/** The time to surface for a single notification: actual sent time, else scheduled send time. */
const notificationTimeIso = (notification) => notification.state === 'sent' && notification.sentAt ? notification.sentAt : notification.sendTime;
export const useAssignmentColumns = () => {
    const translate = useLocalizeMessage();
    const { formatDateTime, formatDateTimeWithZone } = useDateTimeFormat();
    return useMemo(() => {
        // Events and notifications are anchored to the participant's local time, not the site user's
        // browser time zone: an event's c_timezone / a notification's c_tz is the zone it was scheduled
        // in. Render the UTC instant in that zone (showing the zone name so it's unambiguous which local
        // time it is). Fall back to the viewer's time zone only when a record has no stored zone.
        const formatInZone = (iso, timeZone) => timeZone ? formatDateTimeWithZone(iso, { timeZone, showTimeZoneName: 'always' }) : formatDateTime(iso);
        return [
            {
                header: translate('label.timeline.col_activity'),
                accessorKey: 'title',
                cell: (context) => _jsx("span", { "data-testid": 'assignment-activity', children: context.row.original.title }),
            },
            {
                header: translate('label.timeline.col_starts'),
                accessorKey: 'start',
                cell: (context) => {
                    const { start, timezone } = context.row.original;
                    return _jsx("span", { children: formatInZone(start, timezone) });
                },
            },
            {
                header: translate('label.timeline.col_ends'),
                accessorKey: 'end',
                cell: (context) => {
                    const { end, timezone } = context.row.original;
                    return _jsx("span", { children: end ? formatInZone(end, timezone) : EM_DASH });
                },
            },
            {
                header: translate('label.timeline.col_status'),
                accessorKey: 'status',
                cell: (context) => {
                    const { status } = context.row.original;
                    return _jsx(Badge, { variant: STATUS_VARIANT[status], label: translate(`label.timeline.status_${status}`) });
                },
            },
            {
                header: translate('label.timeline.col_notification'),
                id: 'notification',
                cell: (context) => {
                    const { notifications } = context.row.original;
                    if (!notifications.length) {
                        return (_jsx(Text, { size: 'sm', className: 'text-text-secondary', dataTestId: 'assignment-notification-none', children: translate('label.timeline.notif_none') }));
                    }
                    return (_jsx("div", { className: 'flex flex-col items-start gap-1', "data-testid": 'assignment-notification', children: notifications.map((notification) => (_jsx("div", { className: 'flex min-h-[1.75rem] items-center', children: _jsx(Tooltip, { title: notification.name, placement: 'top', wrapperClassName: 'p-0', children: _jsx(Badge, { variant: NOTIF_VARIANT[notification.state], label: translate(`label.timeline.state_${notification.state}`) }) }) }, `${notification.name}-${notification.sendTime}`))) }));
                },
            },
            {
                header: translate('label.timeline.col_notification_time'),
                id: 'notificationTime',
                cell: (context) => {
                    const { notifications } = context.row.original;
                    if (!notifications.length) {
                        return _jsx("span", { children: EM_DASH });
                    }
                    return (_jsx("div", { className: 'flex flex-col gap-1', "data-testid": 'assignment-notification-time', children: notifications.map((notification) => (_jsx("span", { className: 'flex min-h-[1.75rem] items-center', children: formatInZone(notificationTimeIso(notification), notification.timezone) }, `${notification.name}-${notification.sendTime}`))) }));
                },
            },
        ];
    }, [translate, formatDateTime, formatDateTimeWithZone]);
};
